// Evidence Node Model and Reuse Index
//
// A PURE, read-time selector over a control's compliance evidence. Given a
// controlId plus the tenant `documents` and `domainContext`, it returns the
// control's *evidence nodes* — one node per concrete piece of evidence the
// 6-gate engine already surfaces (an approved document, a verified artefact, a
// completed risk action, a satisfied obligation).
//
// The point of this module is REUSE. Evidence is many-to-many with controls:
// the same underlying document/artefact/risk-action/obligation, anchored to a
// TCF control (OBL-B-005), automatically supports every OTHER framework control
// that maps to the same TCF id. `alsoServes` makes that graph explicit so a
// caller can say "this one artefact also covers ISO A.5.14, CIS-3.10, …" without
// re-deriving anything.
//
// Design constraints (see SESSION A1 brief):
//   • No new state keys. Nothing is persisted; this is a read-time projection.
//   • Do NOT reimplement gate evaluation. evaluateControl() is the single source
//     of truth — we read its per-gate `meta` blocks and shape them into nodes.
//   • Derive `alsoServes` from the TCF id → controls (getEquivalentControls) and
//     TCF id → external refs (buildControlIndex().externalRefs) mappings — never
//     by re-collecting evidence across the tenant state.

import { evaluateControl } from '../evaluateControl.js';
import { getEquivalentControls } from '../../constants/controlMappings.js';
import { buildControlIndex } from '../../constants/controlIndex.js';
import { classifyNode } from './nodeClassification.js';
import { predictNodeDecay } from './nodePrediction.js';

/**
 * The concrete evidence types the gates surface today. `kind` on every node is
 * one of these. The taxonomy is intentionally open — acknowledgements, training
 * completions, and integration derivations currently reach a control through
 * these same channels (a training attestation is an artefact → VERIFIED_ARTEFACT;
 * an integration-check derivation surfaces as a satisfied obligation). New kinds
 * are added here as the gate meta begins to surface them separately.
 */
export const EVIDENCE_NODE_KINDS = Object.freeze({
  APPROVED_DOCUMENT: 'approved-document',
  VERIFIED_ARTEFACT: 'verified-artefact',
  COMPLETED_RISK_ACTION: 'completed-risk-action',
  SATISFIED_RISK_OBLIGATION: 'satisfied-risk-obligation',
  FULFILLED_OBLIGATION: 'fulfilled-obligation',
});

/**
 * The reuse graph for a single control: every OTHER control and framework
 * external ref that shares this control's TCF anchor. Because evidence is
 * anchored to the TCF control, any evidence node for `controlId` also serves
 * everything listed here.
 *
 * Derived purely from the TCF mappings the task specifies:
 *   • getEquivalentControls() → sibling control ids across every framework
 *   • buildControlIndex().externalRefs → the framework-attributed ref rows
 *
 * @param {string} controlId  A TCF id (SCF-…) or a native/external framework id.
 * @returns {{ tcfId: string, controls: string[], frameworkRefs: Array<{frameworkId:string, externalId:string, ref?:string}> }}
 */
export function computeAlsoServes(controlId) {
  // getEquivalentControls returns [scfId, ...externalIds], resolving a native
  // id (A.5.14, CIS-3.10) to its TCF anchor first. When the id is unknown to
  // the framework mappings it returns [controlId] unchanged — a control with no
  // TCF anchor simply has no siblings.
  const equivalents = getEquivalentControls(controlId);
  const tcfId = equivalents[0];

  // "Every OTHER control" — drop the control we were asked about; keep its TCF
  // anchor and every sibling framework id.
  const controls = equivalents.filter((id) => id !== controlId);

  const { externalRefs } = buildControlIndex();
  const frameworkRefs = (externalRefs.get(tcfId) || []).filter((r) => r.externalId !== controlId);

  return { tcfId, controls, frameworkRefs };
}

function makeNode(kind, sourceRef, label, gate, backingRefs, alsoServes) {
  // A2 classification layer: tag every node with its evidence `state`
  // (observed/inferred/confirm/upload) and `confidence` (+ reason) so a later
  // auditor narrative can hedge honestly on inferred/proxy evidence.
  const { state, confidence, confidenceReason } = classifyNode({ kind });
  return {
    id: `${kind}:${sourceRef}`,
    kind,
    label: label || sourceRef,
    sourceRef,
    gate,
    backingRefs,
    alsoServes,
    state,
    confidence,
    confidenceReason,
  };
}

/**
 * Return the evidence nodes for a control.
 *
 * Pure and read-only: it calls evaluateControl() to get the authoritative gate
 * `meta`, then projects the evidence-bearing meta blocks into nodes. The gate
 * logic (what counts as evidence, verification filtering, cadence windows) is
 * never duplicated here.
 *
 * A3 predicted dimension: each node also carries a `predicted` block projecting
 * its own freshness clock forward to `auditDate`. A node that is green now but
 * whose freshness lapses on or before the audit date is flagged
 * 'decays-before-audit' so the argument surfaces its future weak links while
 * there is still time to repair them. Nodes are selected as of `asOf` (the
 * "green now" reference); the forecast target is `auditDate` (default = asOf).
 *
 * @param {string} controlId
 * @param {Array}  documents      tenant documents (same array passed to evaluateControl)
 * @param {Object} domainContext  obligations/artefacts/risks/… (same object passed to evaluateControl)
 * @param {number|Date|string} [asOf]  optional evaluation instant (forwarded to evaluateControl)
 * @param {number|Date|string} [auditDate]  optional forecast target for the predicted dimension (default: asOf)
 * @returns {Array<{id, kind, label, sourceRef, gate, backingRefs, alsoServes, state, confidence, confidenceReason, predicted}>}
 */
export function selectEvidenceNodes(controlId, documents, domainContext, asOf, auditDate) {
  const result = evaluateControl(controlId, documents, domainContext, asOf);
  const gates = result.gates || [];
  const gate4 = gates.find((g) => g.id === 'evidence');
  const gate5 = gates.find((g) => g.id === 'obligations');

  // Computed once and shared by reference across every node — the reuse graph
  // is a property of the control's TCF anchor, identical for all its evidence.
  const alsoServes = computeAlsoServes(controlId);

  // A3 predicted dimension: the "green now" reference is the same instant the
  // nodes were selected at (asOf, default now); the forecast target is auditDate
  // (default = that same instant). Freshness clocks read the source records
  // directly, so predictNodeDecay is handed the raw documents + context.
  const nowMs = asOf == null ? Date.now() : new Date(asOf).getTime();
  const predictNowMs = Number.isFinite(nowMs) ? nowMs : Date.now();
  const predictSources = {
    documents,
    artefacts: domainContext?.artefacts || [],
    riskObligations: domainContext?.riskObligations || [],
    artefactSettings: domainContext?.artefactSettings || null,
  };

  const nodes = [];
  const g4 = gate4?.meta || {};

  // Approved documents (Gate 4 — implementation evidence).
  for (const d of g4.approvedDocsDetail || []) {
    nodes.push(
      makeNode(
        EVIDENCE_NODE_KINDS.APPROVED_DOCUMENT,
        d.id,
        d.title,
        'evidence',
        { docId: d.id, version: d.version, approvalDecidedAt: d.approvalDecidedAt },
        alsoServes
      )
    );
  }

  // Verified artefacts (Gate 4). gate4 has already excluded stale external-URL
  // artefacts, so every entry here genuinely counts as evidence.
  for (const a of g4.artefactsDetail || []) {
    nodes.push(
      makeNode(
        EVIDENCE_NODE_KINDS.VERIFIED_ARTEFACT,
        a.id,
        a.title,
        'evidence',
        { artefactId: a.id, artefactType: a.type },
        alsoServes
      )
    );
  }

  // Completed risk actions (Gate 4) — one node per completed action.
  for (const r of g4.risksWithCompletedActions || []) {
    for (const act of r.completedActions || []) {
      nodes.push(
        makeNode(
          EVIDENCE_NODE_KINDS.COMPLETED_RISK_ACTION,
          act.id,
          act.title,
          'evidence',
          { riskId: r.id, riskTitle: r.title, actionId: act.id },
          alsoServes
        )
      );
    }
  }

  // Satisfied risk obligations (Gate 4) — recurring verification work still
  // inside its cadence + grace window — one node per obligation.
  for (const e of g4.satisfiedObligationsDetail || []) {
    for (const ob of e.obligations || []) {
      nodes.push(
        makeNode(
          EVIDENCE_NODE_KINDS.SATISFIED_RISK_OBLIGATION,
          ob.id,
          ob.name,
          'evidence',
          {
            riskId: e.riskId,
            riskName: e.riskName,
            obligationId: ob.id,
            lastSatisfiedAt: ob.lastSatisfiedAt,
          },
          alsoServes
        )
      );
    }
  }

  // Fulfilled catalogue obligations (Gate 5) — a compliance obligation that is
  // currently fulfilled is evidence the control's SoA obligations are met.
  for (const o of gate5?.meta?.linkedObligations || []) {
    if (o.status !== 'fulfilled') continue;
    nodes.push(
      makeNode(
        EVIDENCE_NODE_KINDS.FULFILLED_OBLIGATION,
        o.obligationId,
        o.title,
        'obligations',
        { obligationId: o.obligationId, dueDate: o.dueDate, status: o.status },
        alsoServes
      )
    );
  }

  // Attach the predicted dimension to every node (additive; all existing fields
  // untouched). auditDate defaults to the selection instant, in which case no
  // node can decay "before" the audit and only already-lapsed clocks surface.
  for (const node of nodes) {
    node.predicted = predictNodeDecay(node, predictSources, predictNowMs, auditDate);
  }

  return nodes;
}
